import React from "react";
import { API_BASE_URL } from "@lib/config";
import NavIntradayChart, {
  NavRangeChart,
  type NavIntradaySeries,
  type NavRangeSeries,
} from "./NavIntradayChart";

/**
 * Self-contained NAV chart with its range picker (1D/2D/5D/MTD/QTD/YTD/ITD) — the same
 * chart pair the Portfolio page mounts, packaged so a second page cannot fork the fetch
 * logic or the honesty rules. Added 2026-09-01 for the Signals page's Intraday Book
 * Exposure section (half-width embed).
 *
 * Display-only; its own fail-soft fetch (a failure leaves the previous view on screen —
 * the chart states its own capture counts either way) and never delays the host page.
 *
 * `refreshSignal`: bump to refetch the CURRENT view (the host page passes a
 * once-per-minute counter so the live session grows with the market without polling
 * faster than the 1-minute mark cadence).
 */
const RANGES = ["1d", "2d", "5d", "mtd", "qtd", "ytd", "itd"] as const;

export const NavChartPanel: React.FC<{ refreshSignal?: number; className?: string }> = ({
  refreshSignal,
  className,
}) => {
  // Default 2D on open (2026-09-02, CIO request), matching the Portfolio mount.
  const [range, setRange] = React.useState<string>("2d");
  const [curve, setCurve] = React.useState<NavIntradaySeries | null>(null);
  const [curveLoading, setCurveLoading] = React.useState(false);
  const [rangeSeries, setRangeSeries] = React.useState<NavRangeSeries | null>(null);
  const [rangeLoading, setRangeLoading] = React.useState(false);

  React.useEffect(() => {
    if (range !== "1d") return;
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setCurveLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/risk/nav_intraday`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as NavIntradaySeries;
        if (!cancelled) setCurve(json);
      } catch {
        /* fail-soft: keep the previous curve */
      } finally {
        if (!cancelled) setCurveLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [range, refreshSignal]);

  React.useEffect(() => {
    if (range === "1d") return;
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      setRangeLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/risk/nav_intraday?range=${encodeURIComponent(range)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as NavRangeSeries;
        if (!cancelled) setRangeSeries(json);
      } catch {
        /* fail-soft: keep the previous range view */
      } finally {
        if (!cancelled) setRangeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [range, refreshSignal]);

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center justify-end gap-1 text-[11px]">
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className="rounded px-1.5 py-0.5 uppercase"
            style={{
              border: "1px solid var(--card-border)",
              color: range === r ? "var(--text-primary)" : "var(--text-secondary)",
              background: range === r ? "var(--surface-group)" : "transparent",
              fontWeight: range === r ? 600 : 400,
            }}
            aria-pressed={range === r}
          >
            {r}
          </button>
        ))}
      </div>
      {range === "1d" ? (
        <NavIntradayChart series={curve} loading={curveLoading && !curve} />
      ) : (
        /* Only hand the chart a series matching the SELECTED range — after a switch the
           cached payload still describes the previous range and would render under the
           wrong label until the fetch lands. */
        <NavRangeChart series={rangeSeries?.range === range ? rangeSeries : null} loading={rangeLoading} />
      )}
    </div>
  );
};

export default NavChartPanel;
